import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DryRunResult } from '@mailpilot/shared';
import { CheckCircle2, FlaskConical, Shield, XCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { displayName, listTime } from '../../lib/format';
import { Badge, Card, CardBody, CardHeader, EmptyState, ErrorState, Field, InlineError, Notice, Select, Skeleton, Spinner } from '../ui';
import { Stat, SubSection } from './SettingsPrimitives';
import { WOULD_DO_TEXT } from './ResponderShared';

const THREAD_PARAMS = { view: 'inbox', limit: '20' } as const;

export function ResponderDryRun() {
  const qc = useQueryClient();
  const [threadId, setThreadId] = useState('');
  const threads = useQuery({
    queryKey: qk.threads(THREAD_PARAMS),
    queryFn: () => api('GET /api/threads', { query: THREAD_PARAMS }),
  });

  const run = useMutation({
    mutationFn: async (id: string): Promise<DryRunResult> => {
      const t = await qc.fetchQuery({ queryKey: qk.thread(id), queryFn: () => api('GET /api/threads/:id', { params: { id } }) });
      const incoming = [...t.messages].filter((m) => !m.isOutgoing).sort((a, b) => b.date.localeCompare(a.date))[0];
      if (!incoming) throw new Error('This conversation has no incoming message to test against.');
      return api('POST /api/rules/dry-run', { body: { messageId: incoming.id } });
    },
  });

  const choose = (id: string) => {
    setThreadId(id);
    if (id) run.mutate(id);
    else run.reset();
  };

  const r = run.data;

  return (
    <Card>
      <CardHeader
        icon={<FlaskConical className="h-5 w-5" aria-hidden />}
        title="Dry-run preview"
        description="Pick a recent message to see which rule would match and why. Nothing is sent."
      />
      <CardBody className="space-y-5">
        {threads.isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : threads.isError ? (
          <ErrorState compact error={threads.error} onRetry={() => void threads.refetch()} title="Couldn't load recent messages" />
        ) : !threads.data?.threads.length ? (
          <EmptyState className="!py-4" icon={<FlaskConical className="h-6 w-6" aria-hidden />} title="Nothing to test against yet" description="Connect a mailbox (or add a demo mailbox) and recent messages appear here." />
        ) : (
          <Field label="Recent message">
            {(id) => (
              <Select id={id} value={threadId} onChange={(e) => choose(e.target.value)}>
                <option value="">Choose a message…</option>
                {threads.data.threads.map((t) => (
                  <option key={t.id} value={t.id}>
                    {displayName(t.lastSender)} — {t.subject || '(no subject)'} · {listTime(t.lastMessageAt)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {run.isPending && (
          <div className="flex items-center gap-2 text-[0.8125rem] text-muted">
            <Spinner className="h-4 w-4" /> Evaluating rules…
          </div>
        )}
        <InlineError error={run.error} />

        {r && !run.isPending && (
          <div className="space-y-6 border-t border-line/70 pt-5">
            <Notice tone={r.wouldDo === 'nothing' ? 'info' : r.wouldDo === 'auto_reply' || r.wouldDo === 'acknowledge' ? 'warn' : 'success'}>
              <p className="font-semibold">MailPilot would:</p>
              <p className="mt-0.5">{WOULD_DO_TEXT[r.wouldDo]}</p>
            </Notice>

            <div className="grid gap-6 sm:grid-cols-2">
              <Stat label="Eligible for automation" value={r.eligible ? <Badge tone="success">Yes</Badge> : <Badge tone="danger">No</Badge>} />
              <Stat label="Rules evaluated" value={r.evaluations.length} hint="In priority order" />
            </div>

            {r.blockedBy.length > 0 && (
              <SubSection title="Blocked by safety checks">
                <ul className="space-y-1.5">
                  {r.blockedBy.map((b) => (
                    <li key={b} className="flex items-start gap-2 rounded-xl bg-danger-soft px-3 py-2 text-[0.8125rem] text-danger-ink">
                      <Shield className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                      {b}
                    </li>
                  ))}
                </ul>
              </SubSection>
            )}

            <SubSection title="Rule evaluations" hint="In priority order. The first match decides.">
              {r.evaluations.length === 0 ? (
                <p className="text-[0.8125rem] text-muted">No rules to evaluate.</p>
              ) : (
                <ul className="divide-y divide-line/70">
                  {r.evaluations.map((ev) => (
                    <li key={ev.ruleId} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-center gap-2">
                        {ev.matched ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden /> : <XCircle className="h-4 w-4 shrink-0 text-subtle" aria-hidden />}
                        <span className="font-medium text-ink">{ev.ruleName}</span>
                        <span className="sr-only">{ev.matched ? 'matched' : 'did not match'}</span>
                        {ev.ruleId === r.matchedRuleId && <Badge tone="success">Matched</Badge>}
                      </div>
                      <ul className="mt-1.5 list-disc space-y-0.5 pl-10 text-[0.8125rem] text-muted">
                        {ev.reasons.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </SubSection>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
